

import { useState, useEffect, useCallback, useRef } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import type { EmblaCarouselType, EmblaOptionsType } from 'embla-carousel';
import { ExperienceCard } from './experience-card';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import React from 'react';
import { PlaceHolderImages } from '../lib/placeholder-images';
import { cn } from '../lib/utils';
import { Button } from './ui/button';

const experiences = [
  {
    id: "al-fahidi",
    title: "Visit Al Fahidi District",
    description: "Step back in time and explore Dubai's historic heart."
  },
  {
    id: "yacht-cruise",
    title: "Marina Yacht Cruise",
    description: "Sail through the stunning Dubai Marina on a luxury yacht."
  },
  {
    id: "motiongate",
    title: "Motiongate",
    description: "Experience Hollywood blockbusters at this epic theme park."
  },
  {
    id: "jbr-beach",
    title: "Beach Day at JBR",
    description: "Relax on the sand or enjoy the vibrant beachfront promenade."
  },
  {
    id: "burj-khalifa",
    title: "Burj Khalifa Sky Experience",
    description: "Witness breathtaking views from the world's tallest building."
  },
  {
    id: "dubai-mall",
    title: "Shopping at Dubai Mall",
    description: "Indulge in retail therapy at one of the world's largest malls."
  },
  {
    id: "desert-safari",
    title: "Desert Safari",
    description: "Experience dune bashing, camel rides, and a starlit dinner."
  },
  {
    id: "img-worlds",
    title: "IMG Worlds of Adventure",
    description: "Enter a universe of fun at this massive indoor theme park."
  }
];

const options: EmblaOptionsType = {
  loop: true,
  slidesToScroll: 1,
  align: 'center',
  containScroll: false,
};

export function TopExperiences() {
  const autoplay = useRef(
    Autoplay({ delay: 3000, stopOnInteraction: false })
  );
  const [emblaRef, emblaApi] = useEmblaCarousel(options, [autoplay.current]);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect(emblaApi);
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);

    return () => {
        emblaApi.off('select', onSelect);
    };
  }, [emblaApi, onSelect]);

  const selectedExperience = experiences[selectedIndex];

  return (
    <div className="container mx-auto px-4">
      <div className="text-center mb-12">
        <h2 className="text-4xl md:text-5xl font-bold text-primary">Top Experiences in UAE</h2>
        <p className="mt-4 text-lg text-muted-foreground">Discover unforgettable attractions and activities.</p>
      </div>
      
      <div className="relative">
        <div className="overflow-hidden w-full" ref={emblaRef}>
          <div className="flex -ml-4">
            {experiences.map((experience, index) => {
              const imageData = PlaceHolderImages.find(p => p.id === experience.id);
              if (!imageData) return null;

              return (
                <div 
                  key={experience.id} 
                  className="flex-[0_0_80%] sm:flex-[0_0_50%] md:flex-[0_0_20%] pl-4"
                >
                    <div className={cn(
                      "transition-transform duration-500 ease-in-out",
                      index === selectedIndex ? "scale-100" : "scale-90 opacity-80"
                    )}>
                      <ExperienceCard 
                          imageSrc={imageData.imageUrl}
                          imageAlt={imageData.description}
                          imageHint={imageData.imageHint}
                          isActive={index === selectedIndex}
                      />
                    </div>
                </div>
              );
            })}
          </div>
        </div>

        <Button
            onClick={scrollPrev}
            variant="ghost"
            size="icon"
            className="absolute top-1/2 -left-4 -translate-y-1/2 rounded-full h-16 w-16 bg-white/80 backdrop-blur-sm hover:bg-white shadow-md z-10"
        >
            <ArrowLeft className="h-10 w-10 text-primary" />
        </Button>
        <Button
            onClick={scrollNext}
            variant="ghost"
            size="icon"
            className="absolute top-1/2 -right-4 -translate-y-1/2 rounded-full h-16 w-16 bg-white/80 backdrop-blur-sm hover:bg-white shadow-md z-10"
        >
            <ArrowRight className="h-10 w-10 text-primary" />
        </Button>
      </div>

      <div className="text-center transition-all duration-500 ease-in-out min-h-[180px] flex items-center justify-center max-w-lg mx-auto mt-8">
        {selectedExperience && (
            <div className="animate-fade-in px-8">
                <h3 className="text-4xl font-bold">{selectedExperience.title}</h3>
                <p className="mt-4 text-lg opacity-90 max-w-2xl mx-auto">{selectedExperience.description}</p>
                <Button className="mt-6 text-primary-foreground rounded-full text-lg px-10 py-6">
                    Book Now
                </Button>
            </div>
        )}
      </div>
    </div>
  );
}
